import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Copy, QrCode, Trash, ChartBar, Files, X } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  rascunho: { label: 'Rascunho', cls: 'bg-slate-100 text-slate-600' },
  publicado: { label: 'Publicado', cls: 'bg-brand-green/10 text-brand-green' },
  fechado: { label: 'Fechado', cls: 'bg-brand-gold/20 text-brand-gold-hover' },
};

export default function AdminFormsPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [qrForm, setQrForm] = useState(null);

  const fetchForms = () => {
    axios.get(`${API}/api/forms`).then(({ data }) => setForms(data)).finally(() => setLoading(false));
  };
  useEffect(() => { fetchForms(); }, []);

  const createForm = async () => {
    if (!newTitle.trim()) { toast.error('Indica um título'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api/forms`, { title: newTitle.trim() });
      window.location.href = `/admin/formularios/${data.id}`;
    } catch (e) {
      toast.error('Não foi possível criar o formulário');
      setCreating(false);
    }
  };

  const duplicateForm = async (id) => {
    try {
      await axios.post(`${API}/api/forms/${id}/duplicate`);
      toast.success('Formulário duplicado');
      fetchForms();
    } catch (e) { toast.error('Não foi possível duplicar'); }
  };

  const deleteForm = async (id) => {
    const f = forms.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o formulário "${f?.title || ''}" e todas as respostas associadas (${f?.response_count || 0}).` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/forms/${id}`);
      toast.success('Formulário eliminado');
      fetchForms();
    } catch (e) { toast.error('Não foi possível eliminar'); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copiado');
  };

  return (
    <div className="space-y-6" data-testid="admin-forms-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl text-brand-green font-semibold">Formulários</h1>
          <p className="text-sm text-brand-muted mt-0.5">Formulários públicos personalizáveis, candidaturas, eventos e inscrições.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-border p-4 flex items-center gap-3">
        <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Título do novo formulário (ex: Candidatura Clínicas ao Vivo)"
          onKeyDown={e => e.key === 'Enter' && createForm()}
          className="flex-1 px-3.5 py-2.5 rounded-xl border border-brand-border focus:border-brand-green outline-none text-sm" data-testid="new-form-title-input" />
        <button onClick={createForm} disabled={creating} className="px-4 py-2.5 rounded-full bg-brand-green text-white text-sm font-medium flex items-center gap-1.5 hover:bg-brand-green-hover transition-colors disabled:opacity-60" data-testid="create-form-btn">
          <Plus size={16} /> Criar formulário
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-brand-muted">A carregar...</p>
      ) : forms.length === 0 ? (
        <div className="bg-white rounded-2xl border border-brand-border p-10 text-center">
          <Files size={28} className="text-brand-placeholder mx-auto mb-2" />
          <p className="text-sm text-brand-muted">Ainda não tens formulários. Cria o primeiro acima.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {forms.map(f => (
            <div key={f.id} className="bg-white rounded-2xl border border-brand-border p-4 flex items-center gap-4 flex-wrap" data-testid={`form-card-${f.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/admin/formularios/${f.id}`} className="font-medium text-brand-text hover:text-brand-green no-underline" data-testid={`form-title-link-${f.id}`}>{f.title}</Link>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_META[f.status]?.cls}`} data-testid={`form-status-${f.id}`}>{STATUS_META[f.status]?.label}</span>
                </div>
                <p className="text-xs text-brand-muted mt-1 truncate">{f.public_url}</p>
              </div>
              <Link to={`/admin/formularios/${f.id}/respostas`} className="flex items-center gap-1.5 text-sm text-brand-text hover:text-brand-green no-underline" data-testid={`form-responses-link-${f.id}`}>
                <ChartBar size={16} /> {f.response_count} {f.response_count === 1 ? 'resposta' : 'respostas'}
              </Link>
              <div className="flex items-center gap-2">
                <button onClick={() => copyLink(f.public_url)} title="Copiar link" className="p-2 rounded-lg hover:bg-brand-bg text-brand-muted hover:text-brand-green transition-colors" data-testid={`copy-link-btn-${f.id}`}><Copy size={16} /></button>
                <button onClick={() => setQrForm(f)} title="Gerar QR code" className="p-2 rounded-lg hover:bg-brand-bg text-brand-muted hover:text-brand-green transition-colors" data-testid={`qr-btn-${f.id}`}><QrCode size={16} /></button>
                <button onClick={() => duplicateForm(f.id)} title="Duplicar" className="p-2 rounded-lg hover:bg-brand-bg text-brand-muted hover:text-brand-green transition-colors" data-testid={`duplicate-btn-${f.id}`}><Files size={16} /></button>
                <button onClick={() => deleteForm(f.id)} title="Eliminar" className="p-2 rounded-lg hover:bg-red-50 text-brand-muted hover:text-red-500 transition-colors" data-testid={`delete-form-btn-${f.id}`}><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {qrForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setQrForm(null)}>
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center relative" onClick={e => e.stopPropagation()} data-testid="qr-modal">
            <button onClick={() => setQrForm(null)} className="absolute top-4 right-4" data-testid="close-qr-modal-btn"><X size={18} className="text-brand-muted" /></button>
            <p className="font-medium text-brand-text mb-4">{qrForm.title}</p>
            <div className="flex justify-center mb-4" id="form-qr-code">
              <QRCodeSVG value={qrForm.public_url} size={200} fgColor="#657161" />
            </div>
            <p className="text-xs text-brand-muted break-all mb-4">{qrForm.public_url}</p>
            <button onClick={() => copyLink(qrForm.public_url)} className="px-4 py-2 rounded-full bg-brand-green text-white text-sm font-medium" data-testid="qr-copy-link-btn">Copiar link</button>
          </div>
        </div>
      )}
    </div>
  );
}
